// Bottom-sheet UI for sharing a recipe with family / friends or via the
// OS share API. Self-contained: parent passes the relationships rows,
// the send callback, and an OS-share fallback.
//
// Extracted from CookMode.jsx 2026-05-03.

import { FONT, COLOR } from "../lib/tokens";

export default function ShareRecipeSheet({
  recipe,
  family = [],
  friends = [],
  sentIds,
  onSend,
  onShareNative,
  copiedLink,
  onClose,
}) {
  // Dedupe by otherId in case the relationships hook surfaces the
  // same person twice under different rows (shouldn't happen, but
  // the guard is cheap). Skip any row without a name so the sheet
  // doesn't render ghost entries.
  const familyRows = (family || [])
    .filter(f => f?.otherId && f?.other?.name)
    .filter((f, i, arr) => arr.findIndex(x => x.otherId === f.otherId) === i);
  const friendRows = (friends || [])
    .filter(f => f?.otherId && f?.other?.name)
    .filter((f, i, arr) => arr.findIndex(x => x.otherId === f.otherId) === i);
  const hasAnyone = familyRows.length + friendRows.length > 0;

  const Row = ({ person, isFamily }) => {
    const sent = sentIds?.has(person.otherId);
    return (
      <button
        onClick={() => onSend(person.otherId, isFamily)}
        disabled={sent}
        style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "12px 14px", width: "100%",
          background: sent ? COLOR.leafDeep : COLOR.soil,
          border: `1px solid ${sent ? COLOR.leafDim : COLOR.border}`,
          borderRadius: 12, cursor: sent ? "default" : "pointer",
          textAlign: "left",
        }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: "50%",
          background: COLOR.soil, border: `1px solid ${COLOR.border}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: FONT.itemTitle, fontSize: 16, color: COLOR.ink,
          flexShrink: 0,
        }}>
          {String(person.other?.name || "?").trim().charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: FONT.forma, fontSize: 14,
            color: sent ? "#9bbf9b" : COLOR.ink,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {person.other.name}
          </div>
          <div style={{
            fontFamily: FONT.mono, fontSize: 9,
            color: COLOR.muted, letterSpacing: "0.08em", marginTop: 2,
          }}>
            {isFamily ? "FAMILY" : "FRIEND"}
          </div>
        </div>
        <span style={{
          fontFamily: FONT.mono, fontSize: 10, fontWeight: 700,
          letterSpacing: "0.12em", flexShrink: 0,
          color: sent ? COLOR.leaf : COLOR.gold,
        }}>
          {sent ? "✓ SENT" : "SEND"}
        </span>
      </button>
    );
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "#000000dd", zIndex: 310,
      display: "flex", alignItems: "flex-end",
      maxWidth: 480, margin: "0 auto",
    }}>
      <div style={{
        width: "100%", background: COLOR.ground,
        borderRadius: "20px 20px 0 0", padding: "20px 22px 30px",
        maxHeight: "80vh", display: "flex", flexDirection: "column",
      }}>
        <div style={{ width: 36, height: 4, background: COLOR.border, borderRadius: 2, margin: "0 auto 16px" }} />
        <div style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.gold, letterSpacing: "0.14em" }}>
          SHARE RECIPE
        </div>
        <div style={{ fontFamily: FONT.itemTitle, fontStyle: "italic", fontSize: 22, color: COLOR.ink, fontWeight: 300, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {recipe?.title || "Recipe"}
        </div>

        <div style={{ marginTop: 18, overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
          {familyRows.length > 0 && (
            <>
              <div style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.leafMuted, letterSpacing: "0.12em", marginTop: 2 }}>
                FAMILY
              </div>
              {familyRows.map(f => <Row key={`fam-${f.otherId}`} person={f} isFamily={true} />)}
            </>
          )}
          {friendRows.length > 0 && (
            <>
              <div style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.gold, letterSpacing: "0.12em", marginTop: 8 }}>
                FRIENDS
              </div>
              {friendRows.map(f => <Row key={`fr-${f.otherId}`} person={f} isFamily={false} />)}
            </>
          )}
          {!hasAnyone && (
            <div style={{
              padding: "20px 16px", textAlign: "center",
              fontFamily: FONT.forma, fontSize: 13, color: COLOR.muted,
              background: COLOR.deep, border: `1px solid ${COLOR.edge}`, borderRadius: 12,
            }}>
              No family or friends linked yet. Use the button below to share via text / email / link instead.
            </div>
          )}

          {/* OS share / copy-link fallback — always available. */}
          <div style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.muted, letterSpacing: "0.12em", marginTop: 10 }}>
            OR
          </div>
          <button
            onClick={onShareNative}
            style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "12px 14px", width: "100%",
              background: copiedLink ? COLOR.leafDeep : COLOR.soil,
              border: `1px solid ${copiedLink ? COLOR.leafDim : COLOR.border}`,
              borderRadius: 12, cursor: "pointer", textAlign: "left",
            }}
          >
            <div style={{
              width: 36, height: 36, borderRadius: "50%",
              background: COLOR.goldDeep, border: `1px solid ${COLOR.goldDim}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 18, color: COLOR.gold, flexShrink: 0,
            }}>
              ↗
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: FONT.forma, fontSize: 14,
                color: copiedLink ? COLOR.leafMuted : COLOR.ink,
              }}>
                {copiedLink ? "Link copied to clipboard" : "Share via text, email, or link"}
              </div>
              <div style={{
                fontFamily: FONT.mono, fontSize: 9,
                color: COLOR.muted, letterSpacing: "0.08em", marginTop: 2,
              }}>
                EXTERNAL
              </div>
            </div>
          </button>
        </div>

        <button
          onClick={onClose}
          style={{
            marginTop: 14, padding: "14px",
            background: COLOR.soil, border: `1px solid ${COLOR.border}`,
            color: COLOR.dim, borderRadius: 12,
            fontFamily: FONT.mono, fontSize: 12,
            letterSpacing: "0.08em", cursor: "pointer",
          }}
        >
          DONE
        </button>
      </div>
    </div>
  );
}
